import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lock, User, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { adminLogin, isAuthed } from "../lib/admin";
import { toast } from "sonner";
import { BRAND } from "../lib/constants";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (isAuthed()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter username and password.");
      return;
    }
    setLoading(true);
    try {
      await adminLogin(username, password);
      toast.success("Welcome back, Admin!");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#F8FAF9] flex items-center justify-center px-5 py-10 tassei-grid-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#00B37E] mb-6" data-testid="admin-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to website
        </Link>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-white border border-slate-200 rounded-3xl p-8 md:p-10 shadow-xl shadow-[#0A192F]/5"
        >
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="TASSEI" className="h-12 w-auto object-contain" />
            <div>
              <div className="font-heading font-black text-[#0A192F] text-xl tracking-tight">
                TASSEI<span className="text-[#00B37E]">.in</span> Admin
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-0.5">
                Secure access only
              </div>
            </div>
          </div>

          <h1 className="font-heading text-2xl font-bold text-[#0A192F] mt-8">Sign in</h1>
          <p className="text-sm text-[#64748B] mt-1">Enter your admin credentials to view submissions.</p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="adm-user">Username</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="adm-user"
                  data-testid="admin-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  autoComplete="username"
                  className="pl-9"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="adm-pass">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  id="adm-pass"
                  data-testid="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="pl-9"
                  required
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-6 w-full bg-[#0A192F] hover:bg-[#0F2648] text-white font-semibold rounded-full h-11"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            {loading ? "Signing in..." : "Sign In"}
          </Button>

          <p className="text-xs text-[#64748B] mt-6 text-center">
            Forgot credentials? Contact your developer.
          </p>
        </form>
      </div>
    </div>
  );
}
